import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_QUEUE_ESTIMATE = {
  compatibleCount: null,
  totalInQueue: null,
  playersNeeded: null,
  partySize: 1,
  algorithmEnabled: null,
  geoQueue: null,
  loading: false,
  error: null,
};

export default function useQueueEstimate({
  basePath,
  enabled = true,
  selectedMaps = [],
  selectedServerLocations = [],
  matchmakingOptions = {},
  country = null,
  age = null,
  gender = null,
  elo = null,
  queueMode = "standard",
  partyMemberUserIds = [],
  debounceMs = 500,
  pollIntervalMs = 30000,
}) {
  const [estimate, setEstimate] = useState(DEFAULT_QUEUE_ESTIMATE);
  const abortRef = useRef(null);

  const requestKey = JSON.stringify({
    selectedMaps,
    selectedServerLocations,
    matchmakingOptions,
    country,
    age,
    gender,
    elo,
    queueMode,
    partyMemberUserIds,
  });

  const fetchEstimate = useCallback(async () => {
    if (!enabled || !basePath) {
      setEstimate(DEFAULT_QUEUE_ESTIMATE);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setEstimate((current) => ({
      ...current,
      loading: true,
      error: null,
    }));

    try {
      const response = await fetch(`${basePath}/api/v1/matchmaking/queue/estimate`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: requestKey,
        signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok || !data?.ok) {
        throw new Error(data?.err || data?.message || "Failed to estimate queue");
      }

      setEstimate({
        compatibleCount: data.compatibleCount,
        totalInQueue: data.totalInQueue,
        playersNeeded: data.playersNeeded,
        partySize: data.partySize || 1,
        algorithmEnabled: data.algorithmEnabled,
        geoQueue: data.geoQueue || null,
        loading: false,
        error: null,
      });
    } catch (err) {
      if (err?.name === "AbortError") return;
      setEstimate((current) => ({
        ...current,
        loading: false,
        error: err?.message || "Failed to estimate queue",
      }));
    }
  }, [basePath, enabled, requestKey]);

  useEffect(() => {
    const timeoutId = setTimeout(fetchEstimate, debounceMs);
    return () => clearTimeout(timeoutId);
  }, [debounceMs, fetchEstimate]);

  useEffect(() => {
    if (!enabled || pollIntervalMs <= 0) return undefined;
    const intervalId = setInterval(fetchEstimate, pollIntervalMs);
    return () => clearInterval(intervalId);
  }, [enabled, fetchEstimate, pollIntervalMs]);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  return estimate;
}
